/**
 * Las vistas guardadas: el filtro que alguien arma una vez y usa todos los lunes.
 *
 * «Mis desfavorables sin decidir» es tres controles que un analista vuelve a tocar cada mañana.
 * Guardarlo es guardar el filtro, no los legajos: se resuelve contra la cartera de hoy, así que una
 * vista de hace un mes muestra lo que hay ahora, no lo que había entonces.
 *
 * Viven en el navegador y no en la base a propósito para la etapa 1: son una comodidad de quien las
 * arma, no un objeto del banco, y ponerlas en la base traería a quién pertenecen, quién las ve y
 * qué pasa cuando alguien se va — preguntas reales que todavía no hay que contestar. Cuando el área
 * quiera compartirlas, se mudan; la forma ya es la de un registro.
 */

const KEY = "bancor.views";
const LIMIT = 12;

export interface SavedView {
  name: string;
  /** Los mismos parámetros que la URL de la cartera: la vista es un enlace con nombre. */
  params: Record<string, string>;
}

export function loadViews(): SavedView[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isView).slice(0, LIMIT);
  } catch {
    return [];
  }
}

function isView(value: unknown): value is SavedView {
  const view = value as SavedView | null;
  return (
    !!view && typeof view.name === "string" && !!view.params && typeof view.params === "object"
  );
}

/** Guardar con un nombre que ya existe lo pisa: es lo que espera quien ajusta su propia vista. */
export function saveView(name: string, params: Record<string, string>): SavedView[] {
  const clean = name.trim();
  if (!clean) return loadViews();
  const next = [{ name: clean, params }, ...loadViews().filter((v) => v.name !== clean)].slice(
    0,
    LIMIT,
  );
  write(next);
  return next;
}

export function removeView(name: string): SavedView[] {
  const next = loadViews().filter((v) => v.name !== name);
  write(next);
  return next;
}

function write(views: SavedView[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(views));
  } catch {
    /* sin almacenamiento la vista no sobrevive a la sesión; la pantalla sigue funcionando */
  }
}

/** Cómo se lee una vista guardada en un renglón: para qué sirve sin tener que aplicarla. */
export function describeView(view: SavedView, labels: Record<string, string> = {}): string {
  const parts = Object.entries(view.params)
    .filter(([, value]) => value)
    .map(([key, value]) => labels[`${key}=${value}`] ?? labels[key] ?? value);
  return parts.length ? parts.join(" · ") : "Toda la cartera";
}
